import {
  EmptyStateBase,
  FontAwesomeIcon,
  ListItem,
  Modal,
  ModalProps,
  Search,
  Skeleton,
  Stack,
  useModals,
  useToasts,
} from "@skailer-com/uikit";
import {
  useEmployeeWorkflowRun,
  useEmployeeWorkflowsSearch,
} from "@/queries/employees/use-employee-workflow.query.ts";
import { ApiWorkflowFilter } from "@/types/workflows/workflow.types.ts";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "react-use";

interface EmployeeWorkflowRunModalProps extends ModalProps {
  employeeId: number;
}

const skeletonItems = Array.from({ length: 3 });

export function EmployeeWorkflowRunModal({
  employeeId,
  id,
  ...props
}: EmployeeWorkflowRunModalProps) {
  const { t } = useTranslation();
  const { close } = useModals();
  const { showInfo } = useToasts();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useDebounce(
    () => {
      setDebouncedSearch(search);
    },
    300,
    [search]
  );

  const filter = useMemo<ApiWorkflowFilter>(
    () => ({
      page_number: 1,
      text: debouncedSearch || undefined,
    }),
    [debouncedSearch]
  );

  const { data: workflows, isFetching } = useEmployeeWorkflowsSearch(employeeId, filter);
  const runWorkflow = useEmployeeWorkflowRun();
  const workflowItems = workflows?.items ?? [];

  const handleRunWorkflow = async (workflowId: number) => {
    if (runWorkflow.isPending) return;

    const workflow = workflowItems.find((workflow) => workflow.id === workflowId);

    const result = await runWorkflow.mutateAsync({ employeeId, workflowId });

    if (result.survey) {
      close(id);
      navigate(`/surveys/${result.survey.survey_id}/respond`);
      return;
    }

    showInfo({
      title: t("workflow:employee_workflow_run_success", {
        title: workflow?.title ?? "-",
      }),
    });

    close(id);
  };

  return (
    <Modal title={t("core:new_request")} {...props} id={id} size={"md"} withCloser>
      <Stack>
        <Search
          value={search}
          onInput={(e) => setSearch(e.currentTarget.value)}
          placeholder={t("workflow:search_request")}
          clearable={true}
          onClear={() => setSearch("")}
        />
        <Stack>
          {isFetching &&
            skeletonItems.map((_, index) => (
              <Stack key={index} className={"py-3"} direction={"horizontal"} gap={"smd"}>
                <Skeleton className={"w-9 h-9"} />
                <Stack gap={"xs"} className={"grow"}>
                  <Skeleton className={"h-4 w-1/3"} />
                  <Skeleton className={"h-4 w-2/3"} />
                </Stack>
              </Stack>
            ))}

          {!isFetching && workflowItems.length === 0 && (
            <EmptyStateBase
              emoji={":mag_right:"}
              title={t("workflow:employee_workflows_empty_state_title")}
            />
          )}

          {!isFetching &&
            workflowItems.map((workflow, index) => (
              <ListItem
                key={workflow.id}
                emoji={workflow.emoji ?? ""}
                title={workflow.public_title || workflow.title}
                caption={workflow.public_description || workflow.description || undefined}
                onClick={() => handleRunWorkflow(workflow.id)}
                className={index === workflowItems.length - 1 ? "border-0" : ""}
                valueSlot={
                  runWorkflow.isPending ? (
                    <Skeleton className={"h-4 w-4"} />
                  ) : (
                    <FontAwesomeIcon icon="fa-light fa-chevron-right" />
                  )
                }
              />
            ))}
        </Stack>
      </Stack>
    </Modal>
  );
}
